export const CONSOLIDATOR_TABLE_HEADER_WIDTHS = Object.freeze({
  icon: 24,
  action: 80,
})

export const normalizeConsolidatorTableHeaders = ({
  headers,
  includeActions,
  includeIcon,
  actionWidth,
} = {}) => {
  const headersList = normalizeHeaders({ headers })
  const hasHeaders = headersList.length > 0
  if (!hasHeaders) return []

  const defaultsByValue = getDefaultsByValue({ actionWidth })

  const normalizedWithIcon = normalizeIconHeader({
    headers: headersList,
    includeIcon,
    defaultsByValue,
  })

  const normalizedWithActions = normalizeActionHeader({
    headers: normalizedWithIcon,
    includeActions,
    defaultsByValue,
  })

  return normalizedWithActions.map((headerItem) =>
    normalizeHeaderItem({
      headerItem,
      defaultsByValue,
    })
  )
}

export const buildConsolidatorActionHeader = ({ width } = {}) => {
  const normalizedWidth = normalizeActionWidth({ width })
  return {
    text: 'Ações',
    value: 'action',
    align: 'center',
    sortable: false,
    width: normalizedWidth,
  }
}

export const buildConsolidatorIconHeader = () => ({
  text: '',
  value: 'icon',
  align: 'center',
  width: CONSOLIDATOR_TABLE_HEADER_WIDTHS.icon,
  sortable: false,
})

export const buildMovementRowActions = ({ item, onEdit, onRemove } = {}) => {
  const actions = [
    {
      key: 'edit-movement',
      icon: 'edit',
      tooltip: 'Editar movimentação',
      onClick: onEdit,
    },
    item && item.operacao !== 'Inicial'
      ? {
          key: 'remove-movement',
          icon: 'remove',
          tooltip: 'Remover movimentação',
          onClick: onRemove,
        }
      : null,
  ]
  return withRowActionHandlers({ actions, item })
}

export const buildEventRowActions = ({ item, onEdit, onRemove } = {}) => {
  const actions = [
    {
      key: 'edit-event',
      icon: 'edit',
      tooltip: 'Editar evento',
      onClick: onEdit,
    },
    {
      key: 'remove-event',
      icon: 'remove',
      tooltip: 'Remover evento',
      onClick: onRemove,
    },
  ]
  return withRowActionHandlers({ actions, item })
}

const normalizeHeaderItem = ({ headerItem, defaultsByValue }) => {
  if (!headerItem) return headerItem

  return mergeMissingHeaderFields({
    headerItem,
    defaults: defaultsByValue[headerItem.value],
  })
}

const normalizeIconHeader = ({ headers, includeIcon, defaultsByValue }) => {
  const shouldIncludeIcon = includeIcon !== false
  let foundIcon = false

  const normalizedList = (headers || []).filter((headerItem) => {
    if (!headerItem) return true
    if (headerItem.value !== 'icon') return true
    if (!shouldIncludeIcon) return false
    if (foundIcon) return false
    foundIcon = true
    return true
  })

  if (!shouldIncludeIcon) {
    return normalizedList
  }

  if (foundIcon) {
    return normalizedList
  }

  const defaultIcon = defaultsByValue.icon
  return [defaultIcon, ...normalizedList]
}

const normalizeActionHeader = ({
  headers,
  includeActions,
  defaultsByValue,
}) => {
  const shouldIncludeActions = includeActions !== false
  let foundAction = false

  const normalizedList = (headers || []).filter((headerItem) => {
    if (!headerItem) return true
    if (headerItem.value !== 'action') return true
    if (!shouldIncludeActions) return false
    if (foundAction) return false
    foundAction = true
    return true
  })

  if (!shouldIncludeActions) {
    return normalizedList
  }

  if (foundAction) {
    return normalizedList
  }

  const defaultAction = defaultsByValue.action
  return [...normalizedList, defaultAction]
}

const mergeMissingHeaderFields = ({ headerItem, defaults }) => {
  if (!defaults) return headerItem

  const mergedItem = { ...headerItem }
  Object.entries(defaults).forEach(([key, value]) => {
    if (mergedItem[key] === undefined) {
      mergedItem[key] = value
    }
  })
  return mergedItem
}

const normalizeHeaders = ({ headers }) =>
  Array.isArray(headers) ? headers : []

const getDefaultsByValue = ({ actionWidth }) => ({
  icon: buildConsolidatorIconHeader(),
  action: buildConsolidatorActionHeader({ width: actionWidth }),
})

const normalizeActionWidth = ({ width }) => {
  const isValidWidth = typeof width === 'number' && width > 0
  if (isValidWidth) return width
  return CONSOLIDATOR_TABLE_HEADER_WIDTHS.action
}

const withRowActionHandlers = ({ actions, item }) =>
  actions
    .filter((action) => !!action)
    .map((action) => ({
      ...action,
      handler: () => {
        if (typeof action.onClick === 'function') {
          action.onClick(item)
        }
      },
    }))
